import {
  datadogRum,
  RumEvent,
  RumEventDomainContext,
  RumInitConfiguration,
} from '@datadog/browser-rum'
import { datadogLogs, LogsInitConfiguration } from '@datadog/browser-logs'
import type { User } from '@datadog/browser-core'
import { ErrorHandler, Injectable } from '@angular/core'
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { scrubSensitiveParams } from '@wm-front/core'

/**
 * What the apps put in `environment.datadog`: the RUM init configuration plus the
 * logs-only `forwardErrorsToLogs` flag. Logs reuse the shared keys (token, site, service, env).
 */
export type DatadogConfiguration = Partial<RumInitConfiguration> &
  Pick<LogsInitConfiguration, 'forwardErrorsToLogs'>

/** RUM / logs event fields that can carry a URL */
interface EventWithUrls {
  view?: { url?: string; referrer?: string }
  resource?: { url?: string }
  error?: { resource?: { url?: string } }
  http?: { url?: string }
}

/**
 * Removes auth tokens (`?token=` from login links) from every URL of a RUM or logs event.
 * Mutates the event, as `beforeSend` expects.
 * @param {EventWithUrls} event
 */
export function scrubEventUrls(event: EventWithUrls): void {
  if (event.view) {
    event.view.url = scrubSensitiveParams(event.view.url)
    event.view.referrer = scrubSensitiveParams(event.view.referrer)
  }
  if (event.resource?.url) {
    event.resource.url = scrubSensitiveParams(event.resource.url)
  }
  if (event.error?.resource?.url) {
    event.error.resource.url = scrubSensitiveParams(event.error.resource.url)
  }
  if (event.http?.url) {
    event.http.url = scrubSensitiveParams(event.http.url)
  }
}

/**
 * Builds the RUM init configuration: user interactions (actions and frustration signals),
 * resources and long tasks are tracked, and URLs are scrubbed before sending.
 * The tab identity reaches every event through the `tab` global context (see TabTrackingService).
 * @param {DatadogConfiguration} configuration
 * @returns {RumInitConfiguration}
 */
export function buildRumConfiguration(
  configuration: DatadogConfiguration
): RumInitConfiguration {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { forwardErrorsToLogs, beforeSend, ...rumConfiguration } = configuration
  return {
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    ...rumConfiguration,
    beforeSend: (event: RumEvent, context: RumEventDomainContext) => {
      scrubEventUrls(event as EventWithUrls)
      return beforeSend ? beforeSend(event, context) : true
    },
  } as RumInitConfiguration
}

/**
 * Builds the logs init configuration from the shared keys
 * @param {DatadogConfiguration} configuration
 * @returns {LogsInitConfiguration}
 */
export function buildLogsConfiguration(
  configuration: DatadogConfiguration
): LogsInitConfiguration {
  return {
    clientToken: configuration.clientToken as string,
    site: configuration.site,
    service: configuration.service,
    env: configuration.env,
    version: configuration.version,
    sessionSampleRate: configuration.sessionSampleRate,
    forwardErrorsToLogs: configuration.forwardErrorsToLogs,
    beforeSend: (event) => {
      scrubEventUrls(event as EventWithUrls)
      return true
    },
  }
}

@Injectable({
  providedIn: 'root',
})
export class DatadogService implements ErrorHandler, HttpInterceptor {
  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(configuration: DatadogConfiguration) {
    if (configuration.applicationId) {
      datadogRum.init(buildRumConfiguration(configuration))
      datadogLogs.init(buildLogsConfiguration(configuration))
    }
  }

  /**
   * Angular handler to report errors to datadog rum services
   */
  public handleError(error: Error): void {
    datadogRum.addError(error)
    throw error
  }

  /** Set the user information
   * @param {User} user information
   */
  public setUser(user: User): void {
    datadogRum.setUser(user)
  }

  /** Explicitly report an error to datadog rum, with optional context
   * @param {unknown} error the error to report
   * @param {object} context extra attributes attached to the RUM error event
   */
  public addError(error: unknown, context?: object): void {
    datadogRum.addError(error, context)
  }

  /**
   * Angular HTTP interceptor to report error to rum services
   */
  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        datadogRum.addError(error, {
          'x-request-id': error.headers.get('x-request-id'),
        })
        return throwError(error)
      })
    )
  }
}
